import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { useProfile } from './profile';
import type {
  CompetencyDeclaration,
  SupervisorAssessment,
  CorrectiveAction,
  AssessmentDetail,
  CorrectiveActionDetail,
  CompetencyStatusItem,
} from '@/types';

// F10 Fase 2: the colaborador's per-competency met/pending matrix, derived from
// the activities linked to each competency of their position(s).
export function useMyCompetencyStatus(positionId?: number) {
  return useQuery({
    queryKey: ['competency', 'status', positionId],
    queryFn: () =>
      api.get<CompetencyStatusItem[]>('/me/competency-status', {
        position_id: positionId ? String(positionId) : undefined,
      }),
    select: (res) => res.data,
    placeholderData: keepPreviousData,
  });
}

export function useMyDeclarations(positionId?: number) {
  return useQuery({
    queryKey: ['competency', 'declarations', positionId],
    queryFn: () =>
      api.get<CompetencyDeclaration[]>('/competency', {
        position_id: positionId ? String(positionId) : undefined,
      }),
    select: (res) => res.data,
    placeholderData: keepPreviousData,
  });
}

export function useMyAssessments(positionId?: number) {
  return useQuery({
    queryKey: ['competency', 'assessments', positionId],
    queryFn: () =>
      api.get<SupervisorAssessment[]>('/assessments', {
        position_id: positionId ? String(positionId) : undefined,
      }),
    select: (res) => res.data,
    placeholderData: keepPreviousData,
  });
}

export function useMyCorrectiveActions(positionId?: number) {
  // #7b: scope to the logged-in colaborador. The list endpoint only self-scopes
  // for employee-only roles, so an admin/super_admin viewing the PORTAL would
  // otherwise see every AC. Always pass user_id = self here.
  const { data: profile } = useProfile();
  return useQuery({
    queryKey: ['competency', 'corrective-actions', profile?.id, positionId],
    queryFn: () =>
      api.get<CorrectiveAction[]>('/corrective-actions', {
        user_id: profile?.id ? String(profile.id) : undefined,
        position_id: positionId ? String(positionId) : undefined,
      }),
    select: (res) => res.data,
    enabled: !!profile?.id,
    placeholderData: keepPreviousData,
  });
}

export function useAssessmentDetail(id: number) {
  return useQuery({
    queryKey: ['competency', 'assessments', id],
    queryFn: () => api.get<AssessmentDetail>(`/assessments/${id}`),
    select: (res) => res.data,
    enabled: id > 0,
  });
}

export function useCorrectiveActionDetail(id: number) {
  return useQuery({
    queryKey: ['competency', 'corrective-actions', id],
    queryFn: () => api.get<CorrectiveActionDetail>(`/corrective-actions/${id}`),
    select: (res) => res.data,
    enabled: id > 0,
  });
}

// F6b: the colaborador self-confirms a 'read_material' activity (no external source of
// truth). Course/read_document/quiz complete via their own flow; status is then derived
// server-side and the AC auto-closes at 100%.
export function useCompleteCaActivity() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, activityId }: { id: number; activityId: number }) =>
      api.post<unknown>(`/corrective-actions/${id}/activities/${activityId}/complete`, {}),
    onSuccess: (_res, vars) => {
      qc.invalidateQueries({ queryKey: ['competency', 'corrective-actions', vars.id] });
      qc.invalidateQueries({ queryKey: ['competency', 'corrective-actions'] });
    },
  });
}
